/**
 * Filtros específicos para o gráfico de Pendências
 * Permite filtrar por PendencyType e Fornecedor
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Filter, X, ChevronDown } from 'lucide-react';
import { useProcessosFilters } from '../hooks/useProcessosFilters';

interface PendencyFiltersProps {
  availablePendencyTypes: string[];
  availableSuppliers: string[];
}

export function PendencyFilters({ availablePendencyTypes, availableSuppliers }: PendencyFiltersProps) {
  const pendencyTypes = useProcessosFilters((state) => state.pendencyTypes);
  const suppliers = useProcessosFilters((state) => state.suppliers);
  const setPendencyTypes = useProcessosFilters((state) => state.setPendencyTypes);
  const setSuppliers = useProcessosFilters((state) => state.setSuppliers);
  const clearPendencyFilters = useProcessosFilters((state) => state.clearPendencyFilters);

  const [pendencySearch, setPendencySearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');

  const hasFilters = (pendencyTypes && pendencyTypes.length > 0) || (suppliers && suppliers.length > 0);

  // Filtrar listas baseado na busca
  const filteredPendencyTypes = availablePendencyTypes.filter((p) =>
    p.toLowerCase().includes(pendencySearch.toLowerCase())
  );
  const filteredSuppliers = availableSuppliers.filter((s) =>
    s.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const handlePendencyToggle = (pendencyType: string) => {
    const current = pendencyTypes || [];
    if (current.includes(pendencyType)) {
      const newValue = current.filter((p) => p !== pendencyType);
      setPendencyTypes(newValue.length > 0 ? newValue : null);
    } else {
      setPendencyTypes([...current, pendencyType]);
    }
  };

  const handleSupplierToggle = (supplier: string) => {
    const current = suppliers || [];
    if (current.includes(supplier)) {
      const newValue = current.filter((s) => s !== supplier);
      setSuppliers(newValue.length > 0 ? newValue : null);
    } else {
      setSuppliers([...current, supplier]);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Filtro de Tipo de Pendência */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1">
            <Filter className="h-3.5 w-3.5" />
            Tipo Pendência
            {pendencyTypes && pendencyTypes.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {pendencyTypes.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-auto">
          <DropdownMenuLabel>
            <input
              type="text"
              placeholder="Buscar tipo..."
              value={pendencySearch}
              onChange={(e) => setPendencySearch(e.target.value)}
              className="w-full px-2 py-1 text-sm border rounded bg-background"
            />
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {filteredPendencyTypes.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">Nenhum tipo encontrado</div>
          ) : (
            filteredPendencyTypes.map((pendencyType) => (
              <DropdownMenuCheckboxItem
                key={pendencyType}
                checked={pendencyTypes?.includes(pendencyType) || false}
                onCheckedChange={() => handlePendencyToggle(pendencyType)}
                className="text-xs"
              >
                {pendencyType}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Filtro de Fornecedor */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1">
            <Filter className="h-3.5 w-3.5" />
            Fornecedor
            {suppliers && suppliers.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {suppliers.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-auto">
          <DropdownMenuLabel>
            <input
              type="text"
              placeholder="Buscar fornecedor..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className="w-full px-2 py-1 text-sm border rounded bg-background"
            />
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {filteredSuppliers.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">Nenhum fornecedor encontrado</div>
          ) : (
            filteredSuppliers.map((supplier) => (
              <DropdownMenuCheckboxItem
                key={supplier}
                checked={suppliers?.includes(supplier) || false}
                onCheckedChange={() => handleSupplierToggle(supplier)}
                className="text-xs"
              >
                {supplier}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Botão para limpar filtros */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground hover:text-foreground"
          onClick={clearPendencyFilters}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Limpar
        </Button>
      )}
    </div>
  );
}
